import React, { useEffect, useState } from "react";
import { Button, Card, Col, ListGroup, Row, Table } from "react-bootstrap";
import { GrEdit } from "react-icons/gr";
import EditOutletScreen from "./EditOutletScreen";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import Message from "../components/Message";
import ReUsableModal from "../components/ReUsableModal";
import {
  useAddFoodItemCountMutation,
  useGetFoodItemsQuery,
  useGetMyOutletFoodItemsQuery,
} from "../slices/FoodItemSilce";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { useGetOutletDetailsQuery } from "../slices/OutletSlice";
import CreateFoodItems from "./CreateFoodItems";
import LazyMemberImage from "../components/LazyMemberImage";
import { useAddAdminToOutletMutation } from "../slices/AdminSilce";

const OutletDetailsScreen = () => {
  const { userInfo } = useSelector((state) => state.auth);
  const { outletId } = useParams();

  const [isEditable, setIsEditable] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const [itemName, setItemName] = useState("");
  const [quantity, setQuantity] = useState("");
  const [addQuantity, setAddQuantity] = useState("");
  const [newQuantity, setNewQuantity] = useState("");
  const [selectedItemId, setSelectedItemId] = useState(null);
  const [showItemCountModal, setShowItemCountModal] = useState(false);
  const [createClicked, setCreateClicked] = useState(false);
  const [showAdminModal, setShowAdmilModal] = useState(false);
  const [adminMobile, setAdminMobile] = useState("");

  const {
    data: outletDetails,
    isLoading,
    error,
    refetch,
  } = useGetOutletDetailsQuery({ id: outletId, token: userInfo.token });

  const {
    data: foodItems,
    isLoading: foodItemsLoading,
    error: foodItemsError,
    refetch: foodItemRefetch,
  } = useGetMyOutletFoodItemsQuery({
    token: userInfo?.token,
  });

  const [addFoodItemCount, { isLoading: addingCount }] =
    useAddFoodItemCountMutation();

  const [addAdmin, { isLoading: addAdminLoading }] =
    useAddAdminToOutletMutation();

  const outletFoodItems = foodItems?.filter(
    (item) => item.outletId?._id === outletId
  );

  useEffect(() => {
    if (!isEditable) {
      refetch();
    }
  }, [isEditable]);

  useEffect(() => {
    if (!createClicked) {
      foodItemRefetch();
    }
  }, [createClicked]);

  useEffect(() => {
    if (quantity != null && addQuantity != null) {
      setNewQuantity(Number(quantity) + Number(addQuantity));
    }
  }, [quantity, addQuantity]);

  const handleEdit = () => {
    setIsEditable(true);
  };

  const handleCreateButton = () => {
    setCreateClicked(true);
  };

  // const outletCancelEdit = () => {
  //   setIsEditable(false);
  // };

  const addItemHandler = (item) => {
    setItemName(item?.itemName);
    setQuantity(item?.itemQuantity);
    setNewQuantity(item?.itemQuantity);
    setSelectedItemId(item._id);
    setShowItemCountModal(true);
  };

  const submitAddFoodItem = async () => {
    try {
      const data = {
        _id: selectedItemId,
        itemCount: addQuantity,
        token: userInfo.token,
      };

      await addFoodItemCount(data).unwrap();
      await foodItemRefetch();
      setShowItemCountModal(false);
      toast.success(" Item quantity updated Successfully");
    } catch (err) {
      console.error("Error adding food count:", err);
    }
  };

  const handleCancelEdit = () => {
    setAddQuantity("");
    setNewQuantity("");
    setShowItemCountModal(false);
  };

  const handleNavigateToEdit = (item) => {
    navigate(`/editFoodItem/${item._id}`, { state: { foodItem: item } });
  };

  const handleAddAdmin = () => {
    setShowAdmilModal(true);
  };

  const handleCreateAdmin = () => {
    navigate("/createAdmin", {
      state: { outletId: outletId },
    });
  };

  const handleAdminCancel = () => {
    setShowAdmilModal(false);
  };

  const handleAddAdminSubmit = async () => {
    try {
      const data = {
        outletId: outletId,
        mobileNumber: adminMobile,
        token: userInfo?.token,
      };
      await addAdmin(data).unwrap();
      toast.success("Admin assigned Successfully");
    } catch (err) {
      toast.error(err?.data?.message || err.message);
    }
  };

  return (
    <>
      <Button
        variant="primary"
        className="mb-3"
        onClick={() => navigate("/viewMyOutlets")}
      >
        Back
      </Button>

      <h6>Outlet Details</h6>
      <Row
        style={{
          marginTop: "1rem",
          marginBottom: "0.5rem",
          textAlign: "center",
        }}
      >
        <Col
          xs={12}
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            gap: "1rem",
          }}
        >
          {userInfo.role === "Super Admin" && (
            <>
              <Button
                style={{
                  margin: "0 0.5rem", // subtle horizontal spacing
                  borderRadius: "0.3rem", // softer corners
                }}
                onClick={handleEdit}
              >
                Edit Outlet Details
              </Button>
              <Button
                style={{
                  margin: "0 0.5rem", // subtle horizontal spacing
                  borderRadius: "0.3rem", // softer corners
                  backgroundColor: "orange",
                }}
                onClick={handleCreateButton}
              >
                Create Food Item
              </Button>
              <Button
                style={{
                  margin: "0 0.5rem", // subtle horizontal spacing
                  borderRadius: "0.3rem", // softer corners
                }}
                onClick={handleAddAdmin}
              >
                Add Admin
              </Button>
              <Button
                style={{
                  margin: "0 0.5rem", // subtle horizontal spacing
                  borderRadius: "0.3rem", // softer corners
                  backgroundColor: "green",
                }}
                onClick={handleCreateAdmin}
              >
                Create Admin
              </Button>
            </>
          )}
        </Col>
      </Row>

      {/* Outlet Details in Two Columns */}
      <Row>
        <Col md={6}>
          <ListGroup>
            <ListGroup.Item>
              <strong>Outlet Name:</strong> {outletDetails?.outletName}
            </ListGroup.Item>
            <ListGroup.Item>
              <strong>Company Name:</strong>{" "}
              {outletDetails?.companyId?.companyName || "No company"}
            </ListGroup.Item>
            <ListGroup.Item>
              <strong>Category:</strong> {outletDetails?.outletCategory}
            </ListGroup.Item>
            <ListGroup.Item>
              <strong>Address:</strong> {outletDetails?.outletAddress}
            </ListGroup.Item>
            {/* <ListGroup.Item>
                  <strong>City:</strong> {outletDetails?.companyId?.companyCity}
                </ListGroup.Item> */}
            <ListGroup.Item>
              <strong>Pincode:</strong> {outletDetails?.outletPincode}
            </ListGroup.Item>
          </ListGroup>
        </Col>
        <Col md={6}>
          <ListGroup>
            <ListGroup.Item>
              <strong>State:</strong> {outletDetails?.outletState}
            </ListGroup.Item>
            <ListGroup.Item>
              <strong>Contact:</strong> {outletDetails?.outletContact}
            </ListGroup.Item>

            <ListGroup.Item>
              <strong>Opening Hours:</strong> {outletDetails?.openingHour} -{" "}
              {outletDetails?.closingHour}
            </ListGroup.Item>
            <ListGroup.Item>
              <strong>Opened Days:</strong>{" "}
              {outletDetails?.openedDays?.length > 0
                ? outletDetails?.openedDays.join(", ")
                : "Not available"}
            </ListGroup.Item>

            <ListGroup.Item>
              <strong>Added By:</strong> {outletDetails?.addedBy}
            </ListGroup.Item>
          </ListGroup>
        </Col>
      </Row>
      {isEditable ? (
        <EditOutletScreen outlet={outletDetails} editable={setIsEditable} />
      ) : createClicked ? (
        <CreateFoodItems outlet={outletDetails} creatable={setCreateClicked} />
      ) : (
        <>
          <ReUsableModal
            title="Add Food Count"
            payload={[
              {
                fieldName: "Item Name",
                fieldType: "text",
                fieldValue: itemName,
                setMethod: setItemName,
                disabled: true,
              },
              {
                fieldName: "Existing Quantity",
                fieldType: "number",
                fieldValue: quantity,
                setMethod: setQuantity,
                disabled: true,
              },
              {
                fieldName: "Add Quantity",
                fieldType: "number",
                fieldValue: addQuantity,
                setMethod: setAddQuantity,
              },
              {
                fieldName: "New Quantity",
                fieldType: "number",
                fieldValue: newQuantity,
                setMethod: setNewQuantity,
                disabled: true,
              },
            ]}
            show={showItemCountModal}
            size="md"
            onHide={() => handleCancelEdit(false)}
            onSubmit={submitAddFoodItem}
            submit="Submit"
            cancel="Cancel"
          />
          <ReUsableModal
            title="Add Admin to Outlet"
            payload={[
              {
                fieldName: "Admin MobileNumber",
                fieldType: "number",
                fieldValue: adminMobile,
                setMethod: setAdminMobile,
              },
            ]}
            show={showAdminModal}
            size="md"
            onHide={handleAdminCancel}
            onSubmit={handleAddAdminSubmit}
            submit="Submit"
            cancel="Cancel"
          />
          <br />
          <br />
          <Row>
            <h6>Food Items</h6>
            {outletFoodItems?.length > 0 ? (
              <Table striped bordered hover responsive>
                <thead>
                  <tr>
                    <th></th>
                    <th>Name</th>
                    <th>Original Price</th>
                    <th>Offer Price</th>
                    <th>Discount Percent</th>
                    <th>Discount Amount</th>
                    <th>Available Quantity</th>
                    <th></th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {outletFoodItems.map((item) => (
                    <tr key={item._id}>
                      <td>
                        <LazyMemberImage
                          foodItemId={item._id}
                          imageUpdatedTimeFromDB={item.imageUploadedTime}
                          currentlySelling={item.currentlySelling}
                          photoUploaded={item.imageUploaded}
                        />
                      </td>
                      <td>
                        <a
                          onClick={() =>
                            navigate(`/foodItem/${item._id}`, {
                              state: { from: "outletDetails", showEdit: false },
                            })
                          }
                          style={{
                            cursor: "pointer",
                            textDecoration: "underline",
                          }}
                        >
                          {item.itemName}
                        </a>
                      </td>
                      <td>{item.itemOriginalPrice}</td>
                      <td>{item.itemOfferPrice}</td>
                      <td>{item.discountPercent ?? 0}</td>
                      <td>{item.discountAmount ?? 0}</td>
                      <td>
                        <span
                          style={{
                            color: item.itemQuantity < 10 && "red",
                            fontSize: "1.1rem",
                            marginRight: "0.5rem",
                          }}
                        >
                          {item.itemQuantity}
                        </span>
                        <GrEdit
                          style={{ cursor: "pointer" }}
                          onClick={() => addItemHandler(item)}
                        />
                      </td>

                      <td>
                        <Button onClick={() => handleNavigateToEdit(item)}>
                          Edit
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            ) : (
              <Message>No food items found for this outlet.</Message>
            )}
          </Row>
        </>
      )}
    </>
  );
};

export default OutletDetailsScreen;
